/**
 * "Used by": the workflows (Automations) that refer to
 * something, shown where that something is edited: a stream, an email
 * template, a segment, a rotation, a property, a score band. Read only:
 * each one links to the Workflows page, the only place a workflow is
 * edited. Hidden for a login that may not read workflows.
 */
import { Anchor, Badge, Button, Card, Group, Loader, Menu, Popover, Stack, Text } from '@mantine/core';
import { IconExternalLink, IconRoute } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import {
  useWorkflowsUsedBy, type WorkflowRefType, type WorkflowUsedByResponse,
} from '../../api/workflows';
import { TOKENS } from '../../theme';
import { useVocab, type Vocab } from '../../vocab';
import { HEALTH_LABEL, HealthDot } from './health';

/** How many switched-on workflows use it, in words: "2 active automations". */
export function activeWords(v: Vocab, d: WorkflowUsedByResponse | null | undefined): string {
  const n = d?.active ?? 0;
  return `${n} active ${v.word(n, 'workflow')}`;
}

/** "Used by 3 workflows", counting archived ones too. */
export function usedByWords(v: Vocab, d: WorkflowUsedByResponse | null | undefined): string {
  const n = d?.workflows.length ?? 0;
  return `Used by ${v.count(n, 'workflow')}`;
}

/** The active workflows by name, for a warning: "Welcome" and "Nurture". */
export function activeNames(d: WorkflowUsedByResponse | null | undefined): string[] {
  if (!d) return [];
  return [
    ...d.workflows.filter((w) => w.enabled && !w.archived).map((w) => w.name),
  ];
}

export function useUsedBy(refType: WorkflowRefType, refId: string | null | undefined, enabled = true) {
  return useWorkflowsUsedBy(refType, refId, enabled);
}

type Props = {
  refType: WorkflowRefType;
  refId: string | null | undefined;
  /** The heading: "Workflows using this campaign". */
  title: string;
  /** Says what nothing using it means. */
  empty?: string;
  /** No card around it, for drawers and menus. */
  bare?: boolean;
  testId?: string;
};

export function UsedByPanel({ refType, refId, title, empty, bare, testId }: Props) {
  const v = useVocab();
  const q = useUsedBy(refType, refId);
  if (q.data === null) return null; // not for this login
  const d = q.data;
  const total = d?.workflows.length ?? 0;
  const inner = (
    <Stack gap={8} data-testid={testId ?? `used-by-${refType}`}>
      <Group justify="space-between" wrap="nowrap" gap="xs">
        <Text size="sm" fw={600}>{title}</Text>
        {d && (
          <Badge variant="light" color="gray" radius="sm" tt="none" data-testid="used-by-count">
            {total}
          </Badge>
        )}
      </Group>
      {q.isPending && <Loader size="xs" />}
      {q.error && <Text size="xs" c="dimmed">{q.error.message}</Text>}
      {d && total === 0 && (
        <Text size="xs" c="dimmed">{empty ?? `No ${v.many('workflow')} use this.`}</Text>
      )}
      {d && d.workflows.map((w) => (
        <Group key={w.id} gap={8} wrap="nowrap" data-testid={`used-by-workflow-${w.id}`}>
          <HealthDot state={w.health} />
          <Anchor
            component={Link}
            to={`/automations/${encodeURIComponent(w.id)}`}
            size="sm"
            style={{ color: w.archived ? TOKENS.textMuted : TOKENS.text, overflowWrap: 'anywhere', minWidth: 0 }}
            title={HEALTH_LABEL[w.health]}
          >
            {w.name}
          </Anchor>
          {w.folder && <Text size="xs" c="dimmed" style={{ flex: 'none' }}>{w.folder}</Text>}
          {(w.archived || !w.enabled) && (
            <Badge variant="outline" color="gray" radius="sm" tt="none" size="xs" style={{ flex: 'none' }}>
              {w.archived ? 'Archived' : 'Off'}
            </Badge>
          )}
        </Group>
      ))}
      {d && total > 0 && (
        <Anchor component={Link} to="/automations" size="xs" c="dimmed" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {`Edit ${v.many('workflow')} on the ${v.Many('workflow')} page`} <IconExternalLink size={12} />
        </Anchor>
      )}
    </Stack>
  );
  if (bare) return inner;
  return <Card padding="sm" withBorder>{inner}</Card>;
}

/**
 * For a segment's menu: "Enrolled by 2 workflows", each a link. Mounted
 * only while the menu is open, so a rail of segments asks nothing until a
 * menu opens. Nothing for a login that may not read workflows.
 */
export function EnrolledByMenuItems({ refType, refId }: { refType: 'segment' | 'shared_segment'; refId: string }) {
  const v = useVocab();
  const q = useUsedBy(refType, refId);
  if (!q.data) return null;
  const ws = q.data.workflows;
  return (
    <>
      <Menu.Divider />
      <Menu.Label data-testid={`enrolled-by-${refId}`}>{`Enrolled by ${v.count(ws.length, 'workflow')}`}</Menu.Label>
      {ws.slice(0, 5).map((w) => (
        <Menu.Item key={w.id} component={Link} to={`/automations/${encodeURIComponent(w.id)}`} leftSection={<HealthDot state={w.health} />}>
          {w.name}
        </Menu.Item>
      ))}
      {ws.length > 5 && (
        <Menu.Item component={Link} to="/automations" c="dimmed">{`and ${ws.length - 5} more`}</Menu.Item>
      )}
    </>
  );
}

/**
 * "Used by 2 workflows" as a small button, the list in a popover: for
 * places with no room for a card (a template's top bar, a segment's menu,
 * a drawer's header). Nothing for a login that may not read workflows.
 */
export function UsedByButton({
  refType, refId, title, testId, verb = 'Used by', hideWhenNone = false,
}: {
  refType: WorkflowRefType;
  refId: string | null | undefined;
  title: string;
  testId?: string;
  /** "Used by", "Enrolled by". */
  verb?: string;
  hideWhenNone?: boolean;
}) {
  const v = useVocab();
  const q = useUsedBy(refType, refId);
  if (!q.data) return null;
  const n = q.data.workflows.length;
  if (hideWhenNone && n === 0) return null;
  return (
    <Popover position="bottom-end" width={320} shadow="md" withinPortal>
      <Popover.Target>
        <Button
          size="compact-xs"
          variant="subtle"
          color="gray"
          leftSection={<IconRoute size={13} />}
          data-testid={testId ?? `used-by-button-${refType}`}
        >
          {`${verb} ${v.count(n, 'workflow')}`}
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <UsedByPanel refType={refType} refId={refId} title={title} bare />
      </Popover.Dropdown>
    </Popover>
  );
}
